import React, { useEffect, useState } from 'react';
import { hrAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectGroup, SelectLabel, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Boxes, X } from 'lucide-react';
import { groupSkills } from './skillGrouping';
import { SearchableSelect } from '@/components/SearchableSelect';

const LEVELS = ['S1', 'S2', 'S3', 'S4'];

export default function FunctionalPositionsPage() {
  const { user } = useAuth();
  const canManage = user?.role === 'admin' || user?.app_roles?.hr_training === 'manager';
  const [rows, setRows] = useState([]);
  const [functions, setFunctions] = useState([]);
  const [positions, setPositions] = useState([]);
  const [quals, setQuals] = useState([]);
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dlg, setDlg] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [fp, f, p, q, s] = await Promise.all([
        hrAPI.listFunctionalPositions(), hrAPI.listMaster('functions'),
        hrAPI.listMaster('positions'), hrAPI.listMaster('qualifications'), hrAPI.listSkills(),
      ]);
      setRows(fp.data || []); setFunctions(f.data || []); setPositions(p.data || []);
      setQuals(q.data || []); setSkills(s.data || []);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => setDlg({ function_id: '', position_id: '', alts: [], desired_skills: [] });
  const openEdit = (r) => setDlg({
    id: r.id, function_id: r.function_id, position_id: r.position_id,
    alts: (r.eligibility_alternatives || []).map(a => ({ qualification_id: a.qualification_id, min_experience_years: a.min_experience_years || 0 })),
    desired_skills: (r.desired_skills || []).map(d => ({ skill_id: d.skill_id, min_level: d.min_level })),
  });

  const addAlt = () => setDlg(d => ({ ...d, alts: [...d.alts, { qualification_id: '', min_experience_years: 0 }] }));
  const updAlt = (i, field, val) => setDlg(d => ({ ...d, alts: d.alts.map((a, idx) => idx === i ? { ...a, [field]: val } : a) }));
  const rmAlt = (i) => setDlg(d => ({ ...d, alts: d.alts.filter((_, idx) => idx !== i) }));

  const addSkillRow = () => setDlg(d => ({ ...d, desired_skills: [...d.desired_skills, { skill_id: '', min_level: 'S1' }] }));
  const updSkillRow = (i, field, val) => setDlg(d => ({ ...d, desired_skills: d.desired_skills.map((s, idx) => idx === i ? { ...s, [field]: val } : s) }));
  const rmSkillRow = (i) => setDlg(d => ({ ...d, desired_skills: d.desired_skills.filter((_, idx) => idx !== i) }));

  const save = async () => {
    if (!dlg.function_id || !dlg.position_id) { toast.error('Function and Position are required'); return; }
    const ds = dlg.desired_skills.filter(s => s.skill_id).map(s => ({ skill_id: s.skill_id, min_level: s.min_level }));
    const alts = dlg.alts.filter(a => a.qualification_id).map(a => ({ qualification_id: a.qualification_id, min_experience_years: parseFloat(a.min_experience_years) || 0 }));
    setSaving(true);
    try {
      const payload = {
        function_id: dlg.function_id, position_id: dlg.position_id,
        eligibility_alternatives: alts,
        desired_skills: ds,
      };
      if (dlg.id) await hrAPI.updateFunctionalPosition(dlg.id, payload);
      else await hrAPI.createFunctionalPosition(payload);
      toast.success('Functional position saved'); setDlg(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete "${r.name}"?`)) return;
    try { await hrAPI.deleteFunctionalPosition(r.id); toast.success('Deleted'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="space-y-5" data-testid="hr-functional-positions-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-[#0F172A] flex items-center gap-2"><Boxes className="w-6 h-6 text-pink-600" /> Functional Positions</h1>
          <p className="text-sm text-slate-500 mt-0.5">Unique Function + Position with minimum qualification, experience and desired skills</p>
        </div>
        {canManage && <Button size="sm" className="bg-pink-600 hover:bg-pink-700 text-white gap-1" onClick={openNew} data-testid="hr-fp-add"><Plus className="w-4 h-4" /> Add Functional Position</Button>}
      </div>

      <Card className="border border-slate-200 shadow-none">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50/80">
                <TableHead className="text-xs font-semibold text-slate-500">Functional Position</TableHead>
                <TableHead className="text-xs font-semibold text-slate-500">Eligibility (any of)</TableHead>
                <TableHead className="text-xs font-semibold text-slate-500">Desired Skills</TableHead>
                {canManage && <TableHead className="text-xs font-semibold text-slate-500 w-24 text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={4} className="text-center text-slate-400 py-8 text-sm">Loading...</TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow><TableCell colSpan={4} className="text-center text-slate-400 py-8 text-sm">No functional positions yet</TableCell></TableRow>
              ) : rows.map(r => (
                <TableRow key={r.id} data-testid={`hr-fp-row-${r.id}`}>
                  <TableCell className="text-sm font-medium">{r.name}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap items-center gap-1">
                      {(r.eligibility_alternatives || []).length === 0 ? <span className="text-xs text-slate-400">No requirement</span> :
                        r.eligibility_alternatives.map((a, i) => (
                          <React.Fragment key={i}>
                            {i > 0 && <span className="text-[10px] text-slate-400 font-medium">OR</span>}
                            <Badge variant="outline" className="text-[10px] bg-slate-50">{a.qualification_name || '?'}{a.min_experience_years ? ` + ${a.min_experience_years}yr` : ''}</Badge>
                          </React.Fragment>
                        ))}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {(r.desired_skills || []).length === 0 ? <span className="text-xs text-slate-400">—</span> :
                        r.desired_skills.map((d, i) => <Badge key={i} variant="outline" className="text-[10px]">{d.skill_name} · {d.min_level}</Badge>)}
                    </div>
                  </TableCell>
                  {canManage && (
                    <TableCell className="text-right">
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openEdit(r)} data-testid={`hr-fp-edit-${r.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={() => remove(r)} data-testid={`hr-fp-del-${r.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="hr-fp-dialog">
          <DialogHeader>
            <DialogTitle>{dlg?.id ? 'Edit Functional Position' : 'Add Functional Position'}</DialogTitle>
            <DialogDescription className="text-xs">Function + Position must be a unique combination.</DialogDescription>
          </DialogHeader>
          {dlg && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Function *</Label>
                  <Select value={dlg.function_id || undefined} onValueChange={v => setDlg({ ...dlg, function_id: v })}>
                    <SelectTrigger data-testid="hr-fp-function-select"><SelectValue placeholder="Function" /></SelectTrigger>
                    <SelectContent>{functions.map(f => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Position *</Label>
                  <Select value={dlg.position_id || undefined} onValueChange={v => setDlg({ ...dlg, position_id: v })}>
                    <SelectTrigger data-testid="hr-fp-position-select"><SelectValue placeholder="Position" /></SelectTrigger>
                    <SelectContent>{positions.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label className="text-xs">Eligibility Alternatives (candidate must meet ANY one)</Label>
                  <Button type="button" size="sm" variant="outline" className="h-6 text-[11px] gap-1" onClick={addAlt} data-testid="hr-fp-add-alt"><Plus className="w-3 h-3" /> Add</Button>
                </div>
                <div className="space-y-2">
                  {dlg.alts.length === 0 && <p className="text-[11px] text-slate-400">No requirement — anyone is eligible. Add a qualification + experience combo.</p>}
                  {dlg.alts.map((a, i) => (
                    <div key={i} className="flex items-center gap-2">
                      {i > 0 && <span className="text-[10px] text-slate-400 font-semibold w-6">OR</span>}
                      <Select value={a.qualification_id || undefined} onValueChange={v => updAlt(i, 'qualification_id', v)}>
                        <SelectTrigger className="h-8 text-xs flex-1" data-testid={`hr-fp-alt-qual-${i}`}><SelectValue placeholder="Min qualification" /></SelectTrigger>
                        <SelectContent>{quals.map(q => <SelectItem key={q.id} value={q.id}>L{q.rank ?? '?'} · {q.name}</SelectItem>)}</SelectContent>
                      </Select>
                      <div className="flex items-center gap-1">
                        <span className="text-[10px] text-slate-400">+</span>
                        <Input type="number" className="h-8 text-xs w-16" value={a.min_experience_years} onChange={e => updAlt(i, 'min_experience_years', e.target.value)} data-testid={`hr-fp-alt-exp-${i}`} />
                        <span className="text-[10px] text-slate-400">yr</span>
                      </div>
                      <Button type="button" size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={() => rmAlt(i)} data-testid={`hr-fp-alt-rm-${i}`}><X className="w-3.5 h-3.5" /></Button>
                    </div>
                  ))}
                </div>
                <p className="text-[10px] text-slate-400 mt-1">A higher qualification (by hierarchy level) automatically satisfies a lower minimum.</p>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label className="text-xs">Desired Skills</Label>
                  <Button type="button" size="sm" variant="outline" className="h-6 text-[11px] gap-1" onClick={addSkillRow} data-testid="hr-fp-add-skill"><Plus className="w-3 h-3" /> Add</Button>
                </div>
                <div className="space-y-2">
                  {dlg.desired_skills.length === 0 && <p className="text-[11px] text-slate-400">No desired skills added</p>}
                  {dlg.desired_skills.map((ds, i) => {
                    const rowGroup = ds._group || (ds.skill_id ? (skills.find(s => s.id === ds.skill_id)?.skill_group_id || '') : '');
                    const usedElsewhere = dlg.desired_skills.filter((_, j) => j !== i).map(x => x.skill_id);
                    const rowSkills = skills.filter(s => s.skill_group_id === rowGroup && !usedElsewhere.includes(s.id));
                    return (
                      <div key={i} className="flex items-center gap-2">
                        <Select value={rowGroup || undefined} onValueChange={v => setDlg(d => ({ ...d, desired_skills: d.desired_skills.map((r, idx) => idx === i ? { ...r, _group: v, skill_id: '' } : r) }))}>
                          <SelectTrigger className="h-8 text-xs flex-1" data-testid={`hr-fp-skill-group-${i}`}><SelectValue placeholder="Skill group" /></SelectTrigger>
                          <SelectContent>{groupSkills(skills).map(g => <SelectItem key={g.group_id} value={g.group_id}>{g.group_name}</SelectItem>)}</SelectContent>
                        </Select>
                        <div className="flex-1">
                          <SearchableSelect
                            options={rowSkills.map(s => ({ value: s.id, label: s.name, sub: s.skill_type }))}
                            value={ds.skill_id} onSelect={v => updSkillRow(i, 'skill_id', v)} disabled={!rowGroup}
                            placeholder={rowGroup ? 'Search skill…' : 'Pick group'} testId={`hr-fp-skill-select-${i}`}
                          />
                        </div>
                        <Select value={ds.min_level} onValueChange={v => updSkillRow(i, 'min_level', v)}>
                          <SelectTrigger className="h-8 text-xs w-20" data-testid={`hr-fp-level-select-${i}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{LEVELS.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                        </Select>
                        <Button type="button" size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={() => rmSkillRow(i)}><X className="w-3.5 h-3.5" /></Button>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(null)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-pink-600 hover:bg-pink-700 text-white" data-testid="hr-fp-save">{saving ? 'Saving...' : 'Save'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
